import {
  compareAsc,
  isPast
} from "date-fns";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { sendAccessEmail, updateJobService, updateJobServiceTransition } from "requesters/jobServiceRequester";
import { PRODUCT_CODE_REQUIRE_SCANNER_CADASTRAL } from "../../../../../../utils/constants";
import { getBaseUrl } from "../../../../reducers";
import SendAccessEmailModal from "../../modals/SendAccessEmailModal";
import {
  createDateFilter,
  getMinSelectableDate,
  getDateValidationError,
  VALIDATION_PRESETS
} from "../../../../../../utils/dateValidation";

const ToConfirm = ({ jobService, reloadData }) => {
  const {
    attributes: {
      draftedPrimarySurveyorId,
      bookingDates,
      isGpsAhd,
      isGpsMga,
      isBookInControlSurvey,
      hasControlSurvey,
      state,
      code,
      forProjectX,
      is3DIdJob,
      missingCadastralSurveyor,
      missingScannerSurveyor
    }, id
  } = jobService
  const baseUrl = useSelector(getBaseUrl)
  const [newBookingDates, setNewBookingDates] = useState(bookingDates?.map(d => new Date(d)))
  const [showMessage, setShowMessage] = useState(false)
  const [alertTitle, setAlertTitle] = useState("")
  const [alertText, setAlertText] = useState("")
  const [isDateChanged, setIsDateChanged] = useState(false)
  const [showEmailAccessModal, setShowEmailAccessModal] = useState(false)
  const [missingSurveyor, setMissingSurveyor] = useState(false)
  const [missingSurveyorTypes, setMissingSurveyorTypes] = useState([])
  const [showMissingMessage, setShowMissingMessage] = useState(false)
  const [showErrorMessagesForSendEmail, setShowErrorMessagesForSendEmail] = useState(false)
  const [isMissingCadastral, setIsMissingCadastral] = useState(false)
  const datepickerRefs = useRef([]);
  const [loading, setLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [outsideHoursWarning, setOutsideHoursWarning] = useState(null)
  const [leaveConflictWarning, setLeaveConflictWarning] = useState(null)


  const updateMissingInfo = () => {
    const shouldHaveControlSurveyFromCadastral = missingCadastralSurveyor && !hasControlSurvey
    const shouldHaveControlSurvey3D = forProjectX && is3DIdJob && !hasControlSurvey
    const shouldHaveControlSurvey = shouldHaveControlSurveyFromCadastral || shouldHaveControlSurvey3D
    const isMissingSurveyor =
      (PRODUCT_CODE_REQUIRE_SCANNER_CADASTRAL.includes(code) && forProjectX && (shouldHaveControlSurveyFromCadastral || missingScannerSurveyor)) ||
      shouldHaveControlSurvey3D
    const initMissingTypes = []
    if (shouldHaveControlSurvey) {
      // Use control survey alert instead at the moment
      // initMissingTypes.push("Cadastral")
      setIsMissingCadastral(true)
    } else {
      setIsMissingCadastral(false)
    }
    if (missingScannerSurveyor) initMissingTypes.push("Scanner")

    setMissingSurveyor(isMissingSurveyor)
    setMissingSurveyorTypes(initMissingTypes)
  }

  useEffect(() => {
    updateMissingInfo()
  }, [code, forProjectX, is3DIdJob, missingCadastralSurveyor, missingScannerSurveyor, hasControlSurvey])

  const resetError = () => {
    setShowMessage(false)
    setAlertTitle("")
    setAlertText("")
  }

  const extractErrorMessage = res => {
    const errors = res?.error?.response?.data?.errors
    if (Array.isArray(errors) && errors.length > 0) {
      // Check all errors for "Outside of Work Hours" message
      const outsideHoursError = errors.find(err => {
        const errorText = typeof err === "string" ? err : (err?.detail || err?.title || "")
        return typeof errorText === "string" && errorText.includes("Outside of Work Hours")
      })
      
      if (outsideHoursError) {
        return { type: "outside_hours", message: typeof outsideHoursError === "string" ? outsideHoursError : (outsideHoursError?.detail || outsideHoursError?.title || "") }
      }
      
      // Check all errors for "on leave" message
      const leaveConflictError = errors.find(err => {
        const errorText = typeof err === "string" ? err : (err?.detail || err?.title || "")
        return typeof errorText === "string" && (errorText.includes("on leave") || errorText.includes("on Leave") || errorText.includes("is on leave"))
      })
      
      if (leaveConflictError) {
        return { type: "leave_conflict", message: typeof leaveConflictError === "string" ? leaveConflictError : (leaveConflictError?.detail || leaveConflictError?.title || "") }
      }
      
      // If no specific error found, return first error
      const firstError = errors[0]
      const errorMessage = typeof firstError === "string" ? firstError : (firstError?.detail || firstError?.title || "")
      return { type: "generic", message: errorMessage }
    }

    if (Array.isArray(res?.error?.response?.data) && res.error.response.data.length > 0) {
      const errorMessage = res.error.response.data[0]
      return { type: "generic", message: errorMessage }
    }

    return { type: "generic", message: res?.error?.response?.data?.error || "Something went wrong. Please try again." }
  }

  const JobConfirmBookingHandler = (allowOutsideHours = false, allowLeaveConflict = false) => {
    setConfirmLoading(true)
    const additionalParams = {}
    if (allowOutsideHours) additionalParams.allow_outside_work_hours = true
    if (allowLeaveConflict) additionalParams.allow_leave_conflict = true
    
    updateJobServiceTransition(baseUrl, id, "confirm-booking", null, null, null, null, null, additionalParams)
      .then(res => {
        if (res.status === 200) {
          reloadData()
        } else {
          const errorResult = extractErrorMessage(res)
          if (errorResult.type === "outside_hours") {
            setOutsideHoursWarning(errorResult.message)
          } else if (errorResult.type === "leave_conflict") {
            setLeaveConflictWarning(errorResult.message)
          } else {
            setShowMessage(true)
            setAlertTitle("Job Service can't be confirmed!")
            setAlertText(errorResult.message || "Check the Surveyor skills and the booking time.")
          }
          setConfirmLoading(false)
        }
      })
      .catch(() => {
        setConfirmLoading(false)
      })
  }

  const checkMissingSurveyor = () => {
    if (missingSurveyor) {
      setShowMissingMessage(true)
    } else {
      JobConfirmBookingHandler()
    }
  }

  const checkMissingSurveyorBeforeSendMail = () => {
    if (missingSurveyor) {
      setShowErrorMessagesForSendEmail(true)
    } else {
      setShowEmailAccessModal(true)
    }
  }

  const isPastArray = arr => arr.map(d => isPast(d)).includes(true);

  const editBookingTimeHandler = () => {
    setLoading(true)
    if (!isPastArray(newBookingDates)) {
      const uniqueBookingDates = newBookingDates.filter((date, i, self) => self.findIndex(d => d.getTime() === date.getTime()) === i)

      updateJobService(baseUrl, id, { booking_dates: uniqueBookingDates })
        .then(res => {
          if (res.status === 200) {
            setNewBookingDates(uniqueBookingDates.sort(compareAsc))
            reloadData()
            setIsDateChanged(false)
          }
          setLoading(false)
        })
        // eslint-disable-next-line no-console
        .catch(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
      if (isPastArray(newBookingDates)) {
        setShowMessage(true)
        setAlertTitle("Please choose an available timeslot!")
        setAlertText(getDateValidationError(newBookingDates.find(date => isPast(date)), VALIDATION_PRESETS.BOOKING_DATES))
      }
    }
  }

  const resetDate = () => {
    setNewBookingDates(bookingDates.map(d => new Date(d)))
    setIsDateChanged(false)
  }

  const toastOptions = {
    toastId: "container-inner",
    containerId: "main"
  }

  const addAdditionalDate = () => {
    const lastDate = moment(newBookingDates[newBookingDates.length - 1])
    const nextDate = lastDate.add(1, "days");

    while (nextDate.day() === 0 || nextDate.day() === 6) {
      nextDate.add(1, "days");
    }

    setNewBookingDates([...newBookingDates, nextDate.toDate()]);
    setIsDateChanged(true);
  };


  const removeDate = index => {
    const arr = [...newBookingDates]
    arr.splice(index, 1)
    setNewBookingDates(arr);
    setIsDateChanged(true)
  }

  const sentJobServiceAccessEmail = emailOptions => {
    sendAccessEmail(baseUrl, id, emailOptions)
      .then(res => {
        if (res.status === 200) {
          reloadData()
          toast.success(
            <div>
              <span role="img" aria-label="success-icon">✅</span>
              <span className="ml-2">Email Sent!</span>
            </div>,
            toastOptions
          )
        } else if (res.error) {
          // Handle error returned in response object
          const errorMessage = res.error?.response?.data?.errors?.[0]?.detail 
            || res.error?.response?.data?.message 
            || res.error?.message 
            || "Failed to send confirmation email"
          toast.error(
            <div>
              <span role="img" aria-label="failure-icon">❌</span>
              <span className="ml-2">{errorMessage}</span>
            </div>,
            toastOptions
          )
        }
      })
      .catch(err => {
        const errorMessage = err?.response?.data?.errors?.[0]?.detail 
          || err?.response?.data?.message 
          || err?.message 
          || "Failed to send confirmation email. Please try again."
        toast.error(
          <div>
            <span role="img" aria-label="failure-icon">❌</span>
            <span className="ml-2">{errorMessage}</span>
          </div>,
          toastOptions
        )
      })
  }

  const handleCalendarOpen = key => {
    datepickerRefs.current.forEach((ref, index) => {
      if (key !== index && ref) {
        ref.setOpen(false);
      }
    });
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        {/* {state !== "to_confirm" && (
          <div className="mb-3">
            {newBookingDates?.map((date, index) => (
              <div className="text-left mb-2" key={date}>
                <div className="d-inline-block">
                  <DatePicker
                    required
                    selected={date}
                    onChange={e => {
                      const arr = [...newBookingDates]
                      arr[index] = e
                      setNewBookingDates(arr)
                      setIsDateChanged(true)
                    }}
                    showTimeSelect
                    closeOnScroll={e => e.target === document}
                    className="form-control"
                    dateFormat="dd MMM yyyy, h:mma"
                    minDate={getMinSelectableDate(index > 0 ? newBookingDates[index - 1] : null)}
                    filterDate={createDateFilter(VALIDATION_PRESETS.BOOKING_DATES)}
                    ref={el => {
                      datepickerRefs.current[index] = el;
                    }}
                    onCalendarOpen={() => handleCalendarOpen(index)} />
                  {index !== 0 && (
                    <i
                      className="cursor-pointer ml-2 bi bi-x-circle"
                      onClick={() => removeDate(index)} />
                  )}

                </div>
              </div>
            ))}


            <div className="text-left mt-3">
              <div className="d-inline-block">
                <button className="btn btn-link" type="button" onClick={addAdditionalDate}>Add additional date</button>
              </div>
            </div>

            {isDateChanged && (
              <div className="text-left mt-3">
                <div className="d-inline-block">
                  <button disabled={loading} onClick={editBookingTimeHandler} type="button" className="btn btn-outline-dark mr-2">
                    Update
                    {loading && <Spinner className="ml-1" animation="border" role="status" size="sm" />}
                  </button>
                  <button onClick={resetDate} type="button" className="btn btn-link mx-2">Cancel</button>
                </div>
              </div>
            )}

            <hr />
          </div>
        )} */}
        <div className="text-left mt-3">
          { missingSurveyor && (
            <p className="text-danger">
              { missingSurveyorTypes.length > 0 && (
                <i className="d-block">{`No ${missingSurveyorTypes.join("/")} Surveyor is allocated`}</i>
              )}
              { isMissingCadastral && (
                <i>No Control Survey Created</i>
              )}
            </p>
          )}
          <div className="d-inline-block">
            {(!isGpsAhd && !isGpsMga && !isBookInControlSurvey) && (
              <button className="btn btn-outline-primary" disabled={isDateChanged || confirmLoading} type="button" onClick={checkMissingSurveyorBeforeSendMail}>Send Confirmation Email</button>
            )}

            {(draftedPrimarySurveyorId) && (
              <button className="btn btn-red mt-lg-2 mt-xl-0 ml-2" disabled={isDateChanged || confirmLoading} type="button" onClick={checkMissingSurveyor}>
                Confirm Booking
                {confirmLoading && <Spinner className="ml-1" animation="border" role="status" size="sm" />}
              </button>
            )}
            <SendAccessEmailModal
              show={showEmailAccessModal}
              onHide={() => setShowEmailAccessModal(false)}
              jobService={jobService}
              sentJobServiceAccessEmail={sentJobServiceAccessEmail}
              reloadData={reloadData} />
          </div>
        </div>
        <SweetAlert
          show={!!outsideHoursWarning}
          type="warning"
          title="Outside of Work Hours"
          showCancel
          closeOnClickOutside={false}
          allowEscape={false}
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
          onConfirm={() => {
            setOutsideHoursWarning(null)
            JobConfirmBookingHandler(true, false)
          }}
          onCancel={() => {
            setOutsideHoursWarning(null)
          }}>
          {outsideHoursWarning?.split("\n").map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index} className="mb-1">{line}</p>
          ))}
        </SweetAlert>

        <SweetAlert
          show={!!leaveConflictWarning}
          type="warning"
          title="Surveyor on Leave"
          showCancel
          closeOnClickOutside={false}
          allowEscape={false}
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
          onConfirm={() => {
            setLeaveConflictWarning(null)
            JobConfirmBookingHandler(false, true)
          }}
          onCancel={() => {
            setLeaveConflictWarning(null)
          }}>
          {leaveConflictWarning?.split("\n").map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index} className="mb-1">{line}</p>
          ))}
        </SweetAlert>

        <SweetAlert
          show={showMessage}
          type="error"
          title={alertTitle}
          closeOnClickOutside
          allowEscape={false}
          onConfirm={resetError}>
          {alertText}
        </SweetAlert>

        <SweetAlert
          show={showMissingMessage}
          type="warning"
          title=""
          closeOnClickOutside
          allowEscape={false}
          onConfirm={() => {
            setShowMissingMessage(false)
            JobConfirmBookingHandler()
          }}
          showCancel
          onCancel={() => setShowMissingMessage(false)}>
          { missingSurveyorTypes.length > 0 && (
            <div>
              {`Are you sure you wish to Confirm the Booking with a missing ${missingSurveyorTypes.join("/")} Surveyor?`}
            </div>
          )}
          {isMissingCadastral && (
            <div>Are you sure you wish to Confirm the Booking with No Control Survey?</div>
          )}
        </SweetAlert>

        <SweetAlert
          show={showErrorMessagesForSendEmail}
          type="warning"
          title=""
          closeOnClickOutside
          allowEscape={false}
          onConfirm={() => {
            setShowErrorMessagesForSendEmail(false)
            setShowEmailAccessModal(true)
          }}
          showCancel
          onCancel={() => setShowErrorMessagesForSendEmail(false)}>
          { missingSurveyorTypes.length > 0 && (
            <div>
              {`Are you sure you wish to Send Confirmation with a missing ${missingSurveyorTypes.join("/")} Surveyor?`}
            </div>
          )}
          {isMissingCadastral && (
            <div>Are you sure you wish to Send Confirmation with No Control Survey?</div>
          )}
        </SweetAlert>
      </div>
    </div>
  )
}
export default ToConfirm
